import { LayoutArea, ResponsiveMode } from '../types';

export interface VisibilityConstraint {
  area: LayoutArea;
  minWidth?: number;
  minHeight?: number;
  priority: 'critical' | 'high' | 'medium' | 'low';
  fallbackStrategy: 'modal' | 'drawer' | 'collapse' | 'hidden';
  requiresUserContent: boolean;
}

export interface VisibilityAdjustment {
  area: LayoutArea;
  originalStrategy: string;
  appliedStrategy: string;
  reason: string;
  fallbackUsed: boolean;
}

export interface ViewportConstraints {
  width: number;
  height: number;
  availableWidth: number;
  availableHeight: number;
  mode: ResponsiveMode;
}

/**
 * Service that ensures content is always visible and accessible to users,
 * regardless of device size or layout constraints.
 */
export class ContentVisibilityGuard {
  private static adjustmentLogs: VisibilityAdjustment[] = [];
  private static maxLogEntries = 50;

  /**
   * Default visibility constraints for different layout areas.
   */
  private static readonly DEFAULT_CONSTRAINTS: Record<LayoutArea, VisibilityConstraint> = {
    [LayoutArea.CONTENT]: {
      area: LayoutArea.CONTENT,
      priority: 'critical',
      fallbackStrategy: 'modal',
      requiresUserContent: true,
    },
    [LayoutArea.HEADER]: {
      area: LayoutArea.HEADER,
      priority: 'high',
      fallbackStrategy: 'collapse',
      requiresUserContent: true,
    },
    [LayoutArea.LEFT_SIDEBAR]: {
      area: LayoutArea.LEFT_SIDEBAR,
      minWidth: 200,
      priority: 'medium',
      fallbackStrategy: 'drawer',
      requiresUserContent: true,
    },
    [LayoutArea.RIGHT_SIDEBAR]: {
      area: LayoutArea.RIGHT_SIDEBAR,
      minWidth: 200,
      priority: 'medium',
      fallbackStrategy: 'drawer',
      requiresUserContent: true,
    },
    [LayoutArea.FOOTER]: {
      area: LayoutArea.FOOTER,
      priority: 'low',
      fallbackStrategy: 'collapse',
      requiresUserContent: false,
    },
    [LayoutArea.BREADCRUMB]: {
      area: LayoutArea.BREADCRUMB,
      priority: 'medium',
      fallbackStrategy: 'collapse',
      requiresUserContent: false,
    },
    [LayoutArea.SUBHEADER]: {
      area: LayoutArea.SUBHEADER,
      priority: 'medium',
      fallbackStrategy: 'collapse',
      requiresUserContent: false,
    },
    [LayoutArea.TABS]: {
      area: LayoutArea.TABS,
      priority: 'high',
      fallbackStrategy: 'drawer',
      requiresUserContent: true,
    },
    [LayoutArea.SECONDARY_TABS]: {
      area: LayoutArea.SECONDARY_TABS,
      priority: 'medium',
      fallbackStrategy: 'collapse',
      requiresUserContent: false,
    },
    [LayoutArea.ACTION_BUTTONS]: {
      area: LayoutArea.ACTION_BUTTONS,
      priority: 'high',
      fallbackStrategy: 'drawer',
      requiresUserContent: true,
    },
    [LayoutArea.BOTTOM_PANEL]: {
      area: LayoutArea.BOTTOM_PANEL,
      minHeight: 200,
      priority: 'medium',
      fallbackStrategy: 'drawer',
      requiresUserContent: true,
    },
    [LayoutArea.TOP_PANEL]: {
      area: LayoutArea.TOP_PANEL,
      minHeight: 100,
      priority: 'medium',
      fallbackStrategy: 'collapse',
      requiresUserContent: true,
    },
  };

  /**
   * Ensures all content areas can be displayed given current viewport constraints.
   */
  static ensureContentVisibility(
    activeAreas: LayoutArea[],
    viewport: ViewportConstraints,
    customConstraints?: Partial<Record<LayoutArea, Partial<VisibilityConstraint>>>,
  ): {
    visibleAreas: LayoutArea[];
    fallbackAreas: { area: LayoutArea; strategy: string }[];
    adjustments: VisibilityAdjustment[];
  } {
    const adjustments: VisibilityAdjustment[] = [];
    const visibleAreas: LayoutArea[] = [];
    const fallbackAreas: { area: LayoutArea; strategy: string }[] = [];

    // Merge custom constraints with defaults
    const constraints = this.mergeConstraints(customConstraints);

    // Sort areas by priority (critical first)
    const sortedAreas = activeAreas.sort((a, b) => {
      const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      return priorityOrder[constraints[a].priority] - priorityOrder[constraints[b].priority];
    });

    let remainingWidth = viewport.availableWidth;
    let remainingHeight = viewport.availableHeight;

    // Always ensure CONTENT area is visible
    if (activeAreas.includes(LayoutArea.CONTENT)) {
      visibleAreas.push(LayoutArea.CONTENT);
      // Reserve minimum space for content
      remainingWidth -= 300; // Minimum content width
      remainingHeight -= 200; // Minimum content height
    }

    // Process other areas by priority
    for (const area of sortedAreas) {
      if (area === LayoutArea.CONTENT) continue; // Already processed

      const constraint = constraints[area];

      // Skip areas without constraints (might be new areas not yet defined)
      if (!constraint) {
        continue;
      }

      const canFit = this.canAreaFit(area, constraint, remainingWidth, remainingHeight, viewport.mode);

      if (canFit) {
        visibleAreas.push(area);

        // Deduct estimated space usage
        if (constraint.minWidth) remainingWidth -= constraint.minWidth;
        if (constraint.minHeight) remainingHeight -= constraint.minHeight;

        this.logAdjustment({
          area,
          originalStrategy: 'visible',
          appliedStrategy: 'visible',
          reason: 'Sufficient space available',
          fallbackUsed: false,
        });
      } else {
        // Apply fallback strategy
        const fallbackStrategy = this.determineFallbackStrategy(area, constraint, viewport);
        fallbackAreas.push({ area, strategy: fallbackStrategy });

        this.logAdjustment({
          area,
          originalStrategy: 'visible',
          appliedStrategy: fallbackStrategy,
          reason: `Insufficient space (${remainingWidth}x${remainingHeight})`,
          fallbackUsed: true,
        });

        adjustments.push({
          area,
          originalStrategy: 'visible',
          appliedStrategy: fallbackStrategy,
          reason: `Viewport too small for area requirements`,
          fallbackUsed: true,
        });
      }
    }

    return {
      visibleAreas,
      fallbackAreas,
      adjustments,
    };
  }

  /**
   * Determines if an area can fit in the available space.
   */
  private static canAreaFit(
    area: LayoutArea,
    constraint: VisibilityConstraint,
    availableWidth: number,
    availableHeight: number,
    mode: ResponsiveMode,
  ): boolean {
    // Mobile gets more lenient constraints
    if (mode === ResponsiveMode.MOBILE) {
      return availableWidth > 100 && availableHeight > 50;
    }

    // Check minimum width/height requirements
    if (constraint.minWidth && availableWidth < constraint.minWidth) {
      return false;
    }

    if (constraint.minHeight && availableHeight < constraint.minHeight) {
      return false;
    }

    // Additional logic for specific areas
    switch (area) {
      case LayoutArea.LEFT_SIDEBAR:
      case LayoutArea.RIGHT_SIDEBAR:
        return availableWidth >= 200; // Minimum sidebar width

      case LayoutArea.HEADER:
      case LayoutArea.FOOTER:
        return availableHeight >= 50; // Minimum header/footer height

      default:
        return true;
    }
  }

  /**
   * Determines the best fallback strategy for an area that can't be displayed normally.
   */
  private static determineFallbackStrategy(area: LayoutArea, constraint: VisibilityConstraint, viewport: ViewportConstraints): string {
    // Critical and high priority content should use modal on mobile
    if (viewport.mode === ResponsiveMode.MOBILE) {
      if (constraint.priority === 'critical') {
        return 'modal';
      }
      if (constraint.priority === 'high' && constraint.requiresUserContent) {
        return 'drawer';
      }
    }

    // Use configured fallback strategy
    return constraint.fallbackStrategy;
  }

  /**
   * Merges custom constraints with default constraints.
   */
  private static mergeConstraints(customConstraints?: Partial<Record<LayoutArea, Partial<VisibilityConstraint>>>): Record<LayoutArea, VisibilityConstraint> {
    const merged = { ...this.DEFAULT_CONSTRAINTS };

    if (customConstraints) {
      Object.entries(customConstraints).forEach(([area, custom]) => {
        if (custom) {
          merged[area as LayoutArea] = {
            ...merged[area as LayoutArea],
            ...custom,
          };
        }
      });
    }

    return merged;
  }

  /**
   * Logs visibility adjustments for debugging.
   */
  private static logAdjustment(adjustment: VisibilityAdjustment): void {
    this.adjustmentLogs.push(adjustment);

    // Keep logs under limit
    if (this.adjustmentLogs.length > this.maxLogEntries) {
      this.adjustmentLogs = this.adjustmentLogs.slice(-this.maxLogEntries);
    }

    // Console logging for development
    if (adjustment.fallbackUsed) {
      console.log(`[ContentVisibilityGuard] Fallback applied:`, adjustment);
    }
  }

  /**
   * Gets recent visibility adjustment logs.
   */
  static getAdjustmentLogs(limit?: number): VisibilityAdjustment[] {
    const logs = limit ? this.adjustmentLogs.slice(-limit) : [...this.adjustmentLogs];
    return logs.sort((a, b) => Date.now() - Date.now()); // Most recent first
  }

  /**
   * Validates that critical content is always accessible.
   */
  static validateCriticalContentAccess(
    visibleAreas: LayoutArea[],
    fallbackAreas: { area: LayoutArea; strategy: string }[],
  ): {
    isValid: boolean;
    warnings: string[];
    recommendations: string[];
  } {
    const warnings: string[] = [];
    const recommendations: string[] = [];

    // Check if CONTENT area is visible
    if (!visibleAreas.includes(LayoutArea.CONTENT)) {
      warnings.push('Critical CONTENT area is not visible');
      recommendations.push('Consider using modal fallback for content area');
    }

    // Check if high-priority content has accessible fallbacks
    fallbackAreas.forEach(({ area, strategy }) => {
      const constraint = this.DEFAULT_CONSTRAINTS[area];

      if (constraint.priority === 'critical' && strategy === 'hidden') {
        warnings.push(`Critical area ${area} is hidden with no fallback`);
        recommendations.push(`Use modal or drawer fallback for ${area}`);
      }

      if (constraint.priority === 'high' && constraint.requiresUserContent && strategy === 'hidden') {
        warnings.push(`High-priority user content in ${area} is hidden`);
        recommendations.push(`Provide drawer or modal access for ${area}`);
      }
    });

    return {
      isValid: warnings.length === 0,
      warnings,
      recommendations,
    };
  }

  /**
   * Gets debug information about visibility constraints and adjustments.
   */
  static getDebugInfo(): {
    totalAdjustments: number;
    recentAdjustments: VisibilityAdjustment[];
    fallbackStrategiesUsed: Record<string, number>;
    constraintViolations: number;
  } {
    const recentAdjustments = this.adjustmentLogs.slice(-10);
    const fallbackStrategiesUsed: Record<string, number> = {};
    let constraintViolations = 0;

    this.adjustmentLogs.forEach((adj) => {
      if (adj.fallbackUsed) {
        constraintViolations++;
        fallbackStrategiesUsed[adj.appliedStrategy] = (fallbackStrategiesUsed[adj.appliedStrategy] || 0) + 1;
      }
    });

    return {
      totalAdjustments: this.adjustmentLogs.length,
      recentAdjustments,
      fallbackStrategiesUsed,
      constraintViolations,
    };
  }

  /**
   * Get metrics about visibility management for debugging
   */
  static getVisibilityMetrics() {
    return {
      totalPanels: 0,
      visiblePanels: 0,
      hiddenPanels: 0,
      fallbacksUsed: 0,
      constraints: {
        mobile: 0,
        tablet: 0,
        desktop: 0,
      },
      recentActivity: [],
    };
  }
}
